import React, { useMemo } from 'react';

interface Column {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  format?: (v: any) => string;
}

interface DataTableProps {
  columns: Column[];
  rows?: any[];
  data?: any[];  // 別名互換
  loading?: boolean;
  onRowClick?: (row: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  emptyMessage?: string;
}

const DataTable: React.FC<DataTableProps> = ({ columns, rows, data, loading, onRowClick, onEdit, onDelete, emptyMessage = 'データがありません' }) => {
  const actualRows: any[] = Array.isArray(rows) ? rows : Array.isArray(data) ? data : [];
  if (loading) {
    return <div style={{ padding: 16, color: '#6b7280' }}>読込中...</div>;
  }
  const getAlign = (col: Column, value: any): 'left' | 'right' | 'center' => {
    if (col.align) return col.align;
    if (typeof value === 'number') return 'right';
    return 'left';
  };

  const formattedRows = useMemo(() => {
    return actualRows.map((row) => {
      const formatted: Record<string, string> = {};
      columns.forEach((col) => {
        const raw = row[col.key];
        formatted[col.key] = col.format ? col.format(raw) : (raw != null ? String(raw) : '');
      });
      return { raw: row, formatted };
    });
  }, [actualRows, columns]);

  return (
    <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  backgroundColor: '#e5e7eb',
                  fontWeight: 'bold',
                  padding: '8px 12px',
                  textAlign: col.align || 'left',
                  borderBottom: '2px solid #d1d5db',
                  whiteSpace: 'nowrap',
                  width: col.width || 'auto',
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {formattedRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            formattedRows.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={() => onRowClick?.(item.raw)}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f9fafb',
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    rowIndex % 2 === 0 ? '#ffffff' : '#f9fafb';
                }}
              >
                {columns.map((col) => {
                  const align = getAlign(col, item.raw[col.key]);
                  return (
                    <td
                      key={col.key}
                      style={{
                        padding: '8px 12px',
                        textAlign: align,
                        borderBottom: '1px solid #eee',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.formatted[col.key]}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
